import api from './api';

const BASE = '/internal-tasks';

export interface InternalTask {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  visibility: string;
  assigneeId: string | null;
  assigneeName: string | null;
  createdById: string | null;
  createdByName: string | null;
  customerId: string | null;
  customerName: string | null;
  orderId: string | null;
  orderNumber: string | null;
  displayOrderNumber?: string | null;
  dueDate: string | null;
  attachments: any[];
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskDetail extends InternalTask {
  comments: TaskComment[];
  activities: TaskActivity[];
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string | null;
  authorName: string;
  content: string;
  attachments: any[];
  createdAt: string;
}

export interface TaskActivity {
  id: string;
  taskId: string;
  actorId: string | null;
  actorName: string;
  action: string;
  details: Record<string, any>;
  createdAt: string;
}

export interface TaskUser {
  id: string;
  name: string;
  role: string;
}

export interface TaskFilters {
  status?: string;
  priority?: string;
  assigneeId?: string;
  customerId?: string;
  orderId?: string;
  visibility?: string;
  dueDate?: string;
  search?: string;
}

export const internalTaskApi = {
  getUsers: () =>
    api.get<TaskUser[]>(`${BASE}/users`).then(r => r.data),

  getTasks: (filters: TaskFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const qs = params.toString();
    return api.get<InternalTask[]>(`${BASE}/tasks${qs ? '?' + qs : ''}`).then(r => r.data);
  },

  getTask: (id: string) =>
    api.get<TaskDetail>(`${BASE}/tasks/${id}`).then(r => r.data),

  getTasksByOrder: (orderId: string) =>
    api.get<InternalTask[]>(`${BASE}/tasks/by-order/${orderId}`).then(r => r.data),

  createTask: (data: {
    title: string;
    description?: string;
    priority?: string;
    visibility?: string;
    assigneeId?: string | null;
    customerId?: string | null;
    orderId?: string | null;
    dueDate?: string | null;
  }) =>
    api.post<InternalTask>(`${BASE}/tasks`, data).then(r => r.data),

  updateTask: (id: string, data: Partial<{
    title: string;
    description: string;
    priority: string;
    visibility: string;
    assigneeId: string | null;
    customerId: string | null;
    orderId: string | null;
    dueDate: string | null;
  }>) =>
    api.put<InternalTask>(`${BASE}/tasks/${id}`, data).then(r => r.data),

  updateStatus: (id: string, status: string) =>
    api.patch<InternalTask>(`${BASE}/tasks/${id}/status`, { status }).then(r => r.data),

  addComment: (id: string, content: string) =>
    api.post<TaskComment>(`${BASE}/tasks/${id}/comments`, { content }).then(r => r.data),

  deleteTask: (id: string) =>
    api.delete(`${BASE}/tasks/${id}`).then(r => r.data),
};
